<div class="bw">
  <!-- Progress -->
  <nav class="bw-progress" aria-label="Progreso de la reserva">
    <p class="bw-progress-count">Paso {{ currentStep() }} de {{ totalSteps }}</p>
    <ol class="bw-steps">
      @for (step of steps; track step.number) {
        <li>
          <button
            type="button"
            (click)="goToStep(step.number)"
            [disabled]="step.number > currentStep()"
            class="bw-step"
            [class.is-done]="step.number < currentStep()"
            [class.is-current]="step.number === currentStep()"
            [attr.aria-label]="step.label + (step.number < currentStep() ? ', completado' : step.number === currentStep() ? ', paso actual' : '')"
            [attr.aria-current]="step.number === currentStep() ? 'step' : null">
            <span class="bw-step-bar" aria-hidden="true"></span>
            <span class="bw-step-label">{{ step.label }}</span>
          </button>
        </li>
      }
    </ol>
  </nav>

  <div class="bw-layout">
    <!-- Step content -->
    <div class="bw-main" role="region" aria-live="polite">
      <!-- Step 1: Service -->
      @if (currentStep() === 1) {
        <div class="step-enter">
          <header class="bw-head">
            <h2 class="bw-title">Elige un servicio</h2>
            <p class="bw-subtitle">Al tocarlo pasas directo al siguiente paso.</p>
          </header>

          @if (isLoadingServices) {
            <div class="animate-pulse space-y-3" aria-label="Cargando servicios">
              @for (item of [1, 2, 3]; track item) {
                <div class="bw-skel h-20"></div>
              }
            </div>
          } @else if (services.length === 0) {
            <p class="bw-empty">Este negocio aún no tiene servicios para reservar en línea.</p>
          } @else {
            <ul class="bw-list">
              @for (service of services; track service.serviceId) {
                <li>
                  <app-service-card
                    [service]="service"
                    [isSelected]="selectedService()?.serviceId === service.serviceId"
                    [onClick]="selectService.bind(this, service.serviceId)">
                  </app-service-card>
                </li>
              }
            </ul>
          }
        </div>
      }

      <!-- Step 2: Professional -->
      @if (currentStep() === 2) {
        <div class="step-enter">
          <header class="bw-head">
            <h2 class="bw-title">Elige quién te atiende</h2>
            @if (selectedService(); as svc) {
              <p class="bw-subtitle">Profesionales que hacen {{ svc.name }}.</p>
            }
          </header>

          @if (isLoadingProfessionals) {
            <div class="grid animate-pulse gap-3 sm:grid-cols-2" aria-label="Cargando profesionales">
              @for (item of [1, 2]; track item) {
                <div class="bw-skel h-[72px]"></div>
              }
            </div>
          } @else if (professionals.length === 0) {
            <p class="bw-empty">
              Nadie tiene agenda disponible para este servicio. Vuelve y elige otro, o escríbele al negocio.
            </p>
          } @else {
            <div class="grid gap-3 sm:grid-cols-2">
              @for (professional of professionals; track professional.userId) {
                <app-professional-card
                  [professional]="professional"
                  [isSelected]="selectedProfessional()?.userId === professional.userId"
                  [onClick]="selectProfessional.bind(this, professional.userId)">
                </app-professional-card>
              }
            </div>
          }
        </div>
      }

      <!-- Step 3: Date and time -->
      @if (currentStep() === 3) {
        <div class="step-enter">
          @if (!dateValue()) {
            <header class="bw-head">
              <h2 class="bw-title">Elige el día</h2>
              <p class="bw-subtitle">Los días con color tienen horarios libres.</p>
            </header>

            <app-availability-calendar
              [businessSchedule]="businessSchedule"
              [dayAvailabilities]="dayAvailabilities"
              [selectedDate]="null"
              [primaryColor]="primaryColor"
              [isLoading]="isLoadingMonthAvailability"
              (dateSelected)="onDateSelectedFromCalendar($event)"
              (monthChanged)="onCalendarMonthChanged($event)">
            </app-availability-calendar>
          } @else {
            <header class="bw-head">
              <h2 class="bw-title">Elige la hora</h2>
            </header>

            <button type="button" (click)="clearSelectedDate()" class="bw-date-chip">
              <svg class="size-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span class="bw-date-chip-text">{{ formattedSelectedDate() }}</span>
              <span class="bw-date-chip-action">Cambiar día</span>
            </button>

            <app-time-slot-selector
              [timeSlots]="timeSlots"
              [selectedTime]="timeValue()"
              [isLoading]="isLoadingTimeSlots"
              [primaryColor]="primaryColor"
              (timeSelected)="selectTime($event)">
            </app-time-slot-selector>
          }
        </div>
      }

      <!-- Step 4: Client information -->
      @if (currentStep() === 4) {
        <div class="step-enter">
          <header class="bw-head">
            <h2 class="bw-title">Tus datos</h2>
            <p class="bw-subtitle">Para que el negocio sepa quién llega y pueda contactarte.</p>
          </header>

          <form [formGroup]="form" class="bw-form">
            <div>
              <label class="bw-label" for="client-name">Nombre y apellido</label>
              <input
                id="client-name"
                type="text"
                formControlName="clientName"
                autocomplete="name"
                aria-required="true"
                [attr.aria-invalid]="form.get('clientName')?.invalid && form.get('clientName')?.touched"
                class="bw-input" />
              @if (form.get('clientName')?.invalid && form.get('clientName')?.touched) {
                <p class="bw-error" role="alert">Escribe tu nombre para confirmar la reserva.</p>
              }
            </div>

            <div>
              <label class="bw-label">Teléfono</label>
              <app-phone-input formControlName="phone"></app-phone-input>
            </div>
          </form>

          <!-- On small screens the full ticket is shown before confirming -->
          <div class="bw-inline-ticket">
            <ng-container *ngTemplateOutlet="ticket"></ng-container>
          </div>
        </div>
      }
    </div>

    <!-- Desktop: ticket beside the steps -->
    <aside class="bw-aside" aria-label="Resumen de tu cita">
      <ng-container *ngTemplateOutlet="ticket"></ng-container>
      <ng-container *ngTemplateOutlet="actions"></ng-container>
    </aside>
  </div>

  <!-- Mobile: compact summary and actions within thumb reach -->
  <div class="bw-dock">
    <!-- On the last step the full ticket is already on screen -->
    <div class="bw-dock-summary" aria-hidden="true" [class.hidden]="currentStep() === totalSteps">
      <div class="min-w-0">
        <p class="bw-dock-title">{{ selectedService()?.name || 'Elige un servicio' }}</p>
        @if (dockDetails()) {
          <p class="bw-dock-details">{{ dockDetails() }}</p>
        }
      </div>
      @if (selectedService(); as svc) {
        <p class="bw-dock-price">{{ svc.price | currency:'COP':'symbol-narrow':'1.0-0' }}</p>
      }
    </div>
    <ng-container *ngTemplateOutlet="actions"></ng-container>
  </div>
</div>

<!-- Appointment ticket: fills in as the client chooses -->
<ng-template #ticket>
  <div class="ticket">
    <div class="ticket-head">
      <p class="ticket-title">Tu cita</p>
      @if (businessName) {
        <p class="ticket-business">en {{ businessName }}</p>
      }
    </div>

    <dl class="ticket-rows">
      <div class="ticket-row" [class.is-empty]="!selectedService()">
        <dt>Servicio</dt>
        <dd>{{ selectedService()?.name || 'Por elegir' }}</dd>
      </div>
      <div class="ticket-row" [class.is-empty]="!selectedProfessional()">
        <dt>Profesional</dt>
        <dd>
          @if (selectedProfessional(); as prof) {
            {{ prof.firstName }} {{ prof.lastName }}
          } @else {
            Por elegir
          }
        </dd>
      </div>
      <div class="ticket-row" [class.is-empty]="!dateValue()">
        <dt>Día</dt>
        <dd>{{ formattedSelectedDate() || 'Por elegir' }}</dd>
      </div>
      <div class="ticket-row" [class.is-empty]="!timeValue()">
        <dt>Hora</dt>
        <dd>{{ selectedTimeLabel() || 'Por elegir' }}</dd>
      </div>
    </dl>

    <div class="ticket-perforation" aria-hidden="true"></div>

    <div class="ticket-total">
      <span>
        Total
        @if (selectedService()?.durationMinutes) {
          <span class="ticket-duration">{{ formatDuration(selectedService()!.durationMinutes) }}</span>
        }
      </span>
      <strong>
        @if (selectedService(); as svc) {
          {{ svc.price | currency:'COP':'symbol-narrow':'1.0-0' }}
        } @else {
          –
        }
      </strong>
    </div>
  </div>
</ng-template>

<!-- Back / continue buttons, shared by the dock and the desktop ticket -->
<ng-template #actions>
  <div class="bw-actions">
    <button
      type="button"
      (click)="previousStep()"
      class="bw-btn-back"
      [attr.aria-label]="currentStep() === 1 ? 'Volver al perfil del negocio' : 'Ir al paso anterior'">
      <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
      Atrás
    </button>

    @if (currentStep() < totalSteps) {
      <button type="button" (click)="nextStep()" [disabled]="!canProceedToNextStep()" class="bw-btn-next">
        Continuar
      </button>
    } @else {
      <button
        type="button"
        (click)="submitBooking()"
        [disabled]="!canProceedToNextStep() || isSubmitting"
        class="bw-btn-next">
        @if (isSubmitting) {
          <svg class="size-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Confirmando…
        } @else {
          Confirmar reserva
        }
      </button>
    }
  </div>
</ng-template>
